/**
 * Subscription Service
 * 
 * A clean, maintainable wrapper around RevenueCat Purchases SDK
 * with comprehensive error handling, retry logic, and modern async patterns
 */

import { Capacitor } from '@capacitor/core';
import { Purchases, PurchasesPackage, CustomerInfo } from '@revenuecat/purchases-capacitor';
import type { SubscriptionProduct, SubscriptionTransaction, UserSubscription, SubscriptionStatus, SubscriptionTier } from '@/lib/types';
import { apiClient } from '@/lib/api-client';

// Configuration
const CONFIG = {
  PRODUCT_IDS: {
    MONTHLY: 'com.lullabai.app.monthly',
    PREMIUM: 'com.lullabai.app.premium'
  },
  PURCHASE_TIMEOUT: 60000, // 60 seconds
};

// RevenueCat API Key - should be set via environment or config
let REVENUECAT_API_KEY: string | null = null;

export class SubscriptionError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: any
  ) {
    super(message);
    this.name = 'SubscriptionError';
  }
}

export class SubscriptionService {
  private static instance: SubscriptionService;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private userId: string | null = null;
  private currentSubscription: UserSubscription | null = null;
  private subscriptionListeners = new Set<(subscription: UserSubscription | null) => void>();
  private customerInfoListenerRegistered = false;

  private constructor() { }

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  /**
   * Set the RevenueCat API key
   * Should be called before initialization
   */
  setApiKey(apiKey: string): void {
    REVENUECAT_API_KEY = apiKey;
  }

  /**
   * Initialize the subscription service
   */
  async initialize(userId?: string): Promise<void> {
    // Return existing initialization if in progress
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.isInitialized) {
      // Update user ID if provided
      if (userId && userId !== this.userId) {
        await this.setUserId(userId);
      }
      return Promise.resolve();
    }

    if (!Capacitor.isNativePlatform()) {
      this.isInitialized = true; // Mark as initialized to prevent repeated calls
      return Promise.resolve();
    }

    this.initializationPromise = this._initialize(userId);

    try {
      await this.initializationPromise;
      if (!this.currentSubscription) {
        this.notifySubscriptionChange(null);
      }
    } finally {
      this.initializationPromise = null;
    }
  }

  getCurrentSubscription(): UserSubscription | null {
    return this.currentSubscription;
  }

  subscribeToSubscriptionChanges(
    listener: (subscription: UserSubscription | null) => void
  ): () => void {
    this.subscriptionListeners.add(listener);
    return () => {
      this.subscriptionListeners.delete(listener);
    };
  }

  private notifySubscriptionChange(subscription: UserSubscription | null): void {
    this.currentSubscription = subscription;
    this.subscriptionListeners.forEach((listener) => {
      try {
        listener(subscription);
      } catch (error) {
        console.error('[SubscriptionService] Subscription listener error:', error);
      }
    });
  }

  private registerCustomerInfoListener(): void {
    if (this.customerInfoListenerRegistered) {
      return;
    }

    Purchases.addCustomerInfoUpdateListener((customerInfo) => {
      this.handleCustomerInfoUpdate(customerInfo);
    });

    this.customerInfoListenerRegistered = true;
  }

  private handleCustomerInfoUpdate(customerInfo: CustomerInfo): void {
    const subscription = this.extractSubscriptionFromCustomerInfo(customerInfo);
    if (subscription) {
      this.notifySubscriptionChange(subscription);
    } else {
      this.notifySubscriptionChange(null);
    }
  }

  private async _initialize(userId?: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();

      // Determine API key based on platform
      let apiKey: string;

      if (platform === 'ios') {
        apiKey = REVENUECAT_API_KEY || import.meta.env.VITE_REVENUECAT_IOS_API_KEY || '';
      } else if (platform === 'android') {
        apiKey = REVENUECAT_API_KEY || import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY || '';
      } else {
        throw new SubscriptionError(
          'Unsupported platform for subscriptions',
          'UNSUPPORTED_PLATFORM'
        );
      }

      if (!apiKey) {
        throw new SubscriptionError(
          'RevenueCat API key not configured. Please set VITE_REVENUECAT_IOS_API_KEY or VITE_REVENUECAT_ANDROID_API_KEY in your environment.',
          'API_KEY_MISSING'
        );
      }

      // Configure RevenueCat
      await Purchases.configure({ apiKey });

      this.registerCustomerInfoListener();

      // Set user ID if provided
      if (userId) {
        await this.setUserId(userId);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('[SubscriptionService] Initialization failed:', error);
      throw new SubscriptionError(
        'Failed to initialize subscription service',
        'INIT_FAILED',
        error
      );
    }
  }

  /**
   * Set the user ID for RevenueCat
   */
  async setUserId(userId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      // On web, just store the userId locally without calling RevenueCat
      this.userId = userId;
      return;
    }

    try {
      await Purchases.logIn({ appUserID: userId });
      this.userId = userId;
    } catch (error) {
      console.error('[SubscriptionService] Failed to set user ID:', error);
      throw new SubscriptionError(
        'Failed to set user ID',
        'SET_USER_ID_FAILED',
        error
      );
    }
  }

  /**
   * Get available subscription products
   */
  async getProducts(): Promise<SubscriptionProduct[]> {
    await this.initialize();

    if (!Capacitor.isNativePlatform() || !this.isInitialized) {
      return [];
    }

    try {
      const offerings = await Purchases.getOfferings();

      const currentOffering = offerings.current;

      if (!currentOffering) {
        const allOfferingIds = Object.keys(offerings.all || {});
        const errorDetails = {
          hasOfferings: allOfferingIds.length > 0,
          offeringIds: allOfferingIds,
          expectedProductId: Object.values(CONFIG.PRODUCT_IDS).join(' or '),
          message: allOfferingIds.length === 0
            ? 'No offerings found in RevenueCat. Check: 1) Offerings are created in RevenueCat dashboard, 2) Offering is set as "Current Offering", 3) Products are linked in App Store Connect'
            : `No current offering found. Available offerings: ${allOfferingIds.join(', ')}. Set one as current in RevenueCat dashboard.`
        };

        console.error('[SubscriptionService] No current offering found:', errorDetails);

        throw new SubscriptionError(
          `Configuration error: ${errorDetails.message}. Product IDs: ${Object.values(CONFIG.PRODUCT_IDS).join(', ')}`,
          'CONFIGURATION_ERROR',
          errorDetails
        );
      }

      const products: SubscriptionProduct[] = [];
      const foundProductIds: string[] = [];

      // Extract packages from the offering
      // Return ALL products from RevenueCat, not just those in CONFIG
      for (const pkg of Object.values(currentOffering.availablePackages)) {
        const storeProduct = pkg.product;
        const productId = storeProduct.identifier;
        foundProductIds.push(productId);

        // Try to match product ID to determine tier, but include all products
        const matchingProductId = Object.values(CONFIG.PRODUCT_IDS).find(
          id => id === productId
        );

        const key = matchingProductId
          ? Object.keys(CONFIG.PRODUCT_IDS).find(
            k => CONFIG.PRODUCT_IDS[k as keyof typeof CONFIG.PRODUCT_IDS] === matchingProductId
          ) || 'monthly'
          : 'monthly'; // Default tier if not in config

        // Cast to any to access introductoryDiscount (SDK types may not be complete)
        const storeProductWithIntro = storeProduct as any;

        // Parse price from priceString (e.g., "£8.99" -> 8.99)
        const priceMatch = storeProduct.priceString.match(/[\d.]+/);
        const price = priceMatch ? parseFloat(priceMatch[0]) : storeProduct.price;

        // Robust price formatting:
        // If we have a currency code, use Intl.NumberFormat to ensure correct symbol
        // This fixes issues where device region (US) might cause priceString to be formatted with $
        // even if the actual currency code is GBP.
        let formattedPrice = storeProduct.priceString;
        if (storeProduct.currencyCode) {
          try {
            formattedPrice = new Intl.NumberFormat(navigator.language || 'en-US', {
              style: 'currency',
              currency: storeProduct.currencyCode,
            }).format(price);
          } catch (e) {
            console.warn('[SubscriptionService] Failed to format price with Intl:', e);
            // Fallback to original priceString
          }
        }

        // Extract period information from product title or description
        // Common patterns: "Monthly", "1 Month", "Yearly", "1 Year", etc.
        let period = 'month'; // default
        let subscriptionPeriod = '1 month';
        let tier: SubscriptionTier = 'monthly';

        const titleLower = (storeProduct.title || '').toLowerCase();
        const descLower = (storeProduct.description || '').toLowerCase();

        if (titleLower.includes('year') || descLower.includes('year')) {
          period = 'year';
          subscriptionPeriod = '1 year';
          tier = 'yearly';
        } else if (titleLower.includes('month') || descLower.includes('month')) {
          period = 'month';
          subscriptionPeriod = '1 month';
          tier = 'monthly';
        } else if (titleLower.includes('week') || descLower.includes('week')) {
          period = 'week';
          subscriptionPeriod = '1 week';
          tier = 'monthly'; // Weekly subscriptions map to monthly tier
        } else if (titleLower.includes('lifetime') || descLower.includes('lifetime') || titleLower.includes('one-time')) {
          period = 'lifetime';
          subscriptionPeriod = 'lifetime';
          tier = 'lifetime';
        }

        // Determine plan name using only allowed keywords from product title
        const allowedKeywords = ['annual', 'yearly', 'premium', 'monthly'];
        const extractedKeywords: string[] = [];

        if (storeProduct.title) {
          for (const keyword of allowedKeywords) {
            const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'i');
            if (keywordRegex.test(storeProduct.title)) {
              const capitalizedKeyword = keyword.charAt(0).toUpperCase() + keyword.slice(1);
              if (!extractedKeywords.includes(capitalizedKeyword)) {
                extractedKeywords.push(capitalizedKeyword);
              }
            }
          }
        }

        const planName = extractedKeywords.length > 0
          ? extractedKeywords.join(' ')
          : `${key.charAt(0).toUpperCase() + key.slice(1)} Subscription`;

        // Extract intro offer from iOS (introductoryDiscount) or Android (introPrice or defaultOption.introPhase)
        const iosIntroDiscount = storeProductWithIntro.introductoryDiscount;
        // Android: introPrice is at root level, defaultOption is also at root (not inside subscriptionOptions)
        const androidIntroPrice = storeProductWithIntro.introPrice;
        const androidDefaultOption = storeProductWithIntro.defaultOption;
        const androidIntroPhase = androidDefaultOption?.introPhase;

        // Try iOS structure first, then Android introPrice, then Android defaultOption.introPhase
        let introPrice: number | null = null;
        let introPriceString: string | null = null;
        let introPricePeriod: string | null = null;
        let introPricePeriodUnit: string | null = null;
        let introPricePeriodNumberOfUnits: number | null = null;

        if (iosIntroDiscount) {
          // iOS structure
          introPrice = iosIntroDiscount.price ?? null;
          introPriceString = iosIntroDiscount.localizedIntroductoryPriceString ?? null;
          introPricePeriod = iosIntroDiscount.subscriptionPeriod ?? null;
          introPricePeriodUnit = iosIntroDiscount.subscriptionPeriod?.unit ?? null;
          introPricePeriodNumberOfUnits = iosIntroDiscount.subscriptionPeriod?.value ?? null;
        } else if (androidIntroPrice) {
          // Android structure - introPrice at root level
          // Structure: { price: 2.99, priceString: "£2.99", period: "P1M", periodUnit: "MONTH", periodNumberOfUnits: 1 }
          introPrice = androidIntroPrice.price ?? null;
          introPriceString = androidIntroPrice.priceString ?? null;
          introPricePeriod = androidIntroPrice.period ?? null;
          introPricePeriodUnit = androidIntroPrice.periodUnit?.toLowerCase() ?? null;
          introPricePeriodNumberOfUnits = androidIntroPrice.periodNumberOfUnits ?? null;
        } else if (androidIntroPhase) {
          // Fallback: Android defaultOption.introPhase structure
          // Structure: { price: { formatted: "£2.99", amountMicros: 2990000 }, billingPeriod: { unit: "MONTH", value: 1 } }
          introPrice = androidIntroPhase.price?.amountMicros
            ? androidIntroPhase.price.amountMicros / 1_000_000
            : null;
          introPriceString = androidIntroPhase.price?.formatted ?? null;

          const billingPeriod = androidIntroPhase.billingPeriod;
          if (billingPeriod) {
            introPricePeriod = billingPeriod.iso8601 ?? null;
            introPricePeriodUnit = billingPeriod.unit?.toLowerCase() ?? null;
            introPricePeriodNumberOfUnits = billingPeriod.value ?? null;
          }
        }

        products.push({
          id: storeProduct.identifier,
          title: planName || `${key.charAt(0).toUpperCase() + key.slice(1)} Subscription`,
          description: storeProduct.description || `LullabAI ${tier} subscription`,
          price,
          priceString: formattedPrice,
          currency: storeProduct.currencyCode || 'GBP',
          tier,
          platform: Capacitor.getPlatform() as 'ios' | 'android',
          productId: storeProduct.identifier,
          period,
          subscriptionPeriod,
          introPrice,
          introPriceString,
          introPricePeriod,
          introPricePeriodUnit,
          introPricePeriodNumberOfUnits
        });

      }

      // If no products found in offering, provide helpful error
      if (products.length === 0 && foundProductIds.length > 0) {
        console.warn('[SubscriptionService] Found products in offering but none were processed:', foundProductIds);
        // Don't throw error - just log warning. Products might still be valid but not match expected format
      }

      if (products.length === 0) {
        const errorMessage = `No packages found in current offering "${currentOffering.identifier}". Check that packages are added to the offering in RevenueCat dashboard.`;
        console.error('[SubscriptionService]', errorMessage);
        throw new SubscriptionError(
          errorMessage,
          'NO_PACKAGES_IN_OFFERING',
          {
            offeringId: currentOffering.identifier,
            expectedProductId: Object.values(CONFIG.PRODUCT_IDS).join(' or ')
          }
        );
      }

      return products;
    } catch (error: any) {
      // Re-throw SubscriptionError as-is
      if (error instanceof SubscriptionError) {
        throw error;
      }

      // Handle RevenueCat-specific errors
      const errorMessage = error?.message || String(error);
      let errorCode = 'PRODUCT_FETCH_FAILED';
      let helpfulMessage = 'Failed to fetch subscription products';

      // Parse common RevenueCat errors
      if (errorMessage.includes('configuration') || errorMessage.includes('configure')) {
        errorCode = 'CONFIGURATION_ERROR';
        helpfulMessage = `RevenueCat configuration error: ${errorMessage}. Check: 1) Products are added in RevenueCat dashboard, 2) Products are linked to App Store Connect, 3) Offering is set as current, 4) Bundle ID matches in all three places (Xcode: com.lullabai.app, App Store Connect, RevenueCat)`;
      } else if (errorMessage.includes('product') && (errorMessage.includes('fetch') || errorMessage.includes('could not be fetched'))) {
        errorCode = 'PRODUCT_FETCH_FAILED';
        helpfulMessage = `Product fetch failed: ${errorMessage}. This usually means products in RevenueCat dashboard couldn't be fetched from App Store Connect. Check: 1) Product exists in App Store Connect with status "Ready to Submit" or "Approved", 2) Product ID matches exactly: ${Object.values(CONFIG.PRODUCT_IDS).join(' or ')}, 3) Bundle ID matches: com.lullabai.app, 4) App Store Connect API is connected in RevenueCat`;
      }

      console.error('[SubscriptionService] Error fetching products:', {
        message: errorMessage,
        code: errorCode,
        originalError: error,
        stack: error?.stack
      });

      throw new SubscriptionError(
        helpfulMessage,
        errorCode,
        error
      );
    }
  }

  /**
   * Purchase a product with retry logic
   */
  async purchaseProduct(productId: string): Promise<SubscriptionTransaction> {
    await this.initialize();

    if (!Capacitor.isNativePlatform() || !this.isInitialized) {
      throw new SubscriptionError(
        'Service not initialized or not on native platform',
        'NOT_INITIALIZED'
      );
    }

    // Attempt purchase
    try {
      return await this._attemptPurchase(productId);
    } catch (error) {
      console.error('[SubscriptionService] Purchase failed:', error);
      throw error;
    }
  }

  private async _attemptPurchase(productId: string): Promise<SubscriptionTransaction> {
    try {
      // Get offerings to find the package
      const offerings = await Purchases.getOfferings();
      const currentOffering = offerings.current;

      if (!currentOffering) {
        throw new SubscriptionError(
          'No offerings available',
          'NO_OFFERINGS'
        );
      }

      // Find the package for this product
      let targetPackage: PurchasesPackage | null = null;

      for (const pkg of Object.values(currentOffering.availablePackages)) {
        if (pkg.product.identifier === productId) {
          targetPackage = pkg;
          break;
        }
      }

      if (!targetPackage) {
        throw new SubscriptionError(
          `Product ${productId} not found in offerings`,
          'PRODUCT_NOT_FOUND'
        );
      }

      // Purchase the package
      const purchaseResult = await Purchases.purchasePackage({
        aPackage: targetPackage
      });

      // Get the latest transaction from customer info
      const transaction = await this.createTransactionFromCustomerInfo(
        purchaseResult.customerInfo,
        productId,
        'purchased'
      );

      // Sync with backend (optional - RevenueCat handles validation, but you may want to sync)
      // This updates the backend with the purchase and sets user profile subscription to 'active'
      try {
        await this.syncWithBackend(purchaseResult.customerInfo);
      } catch (validationError) {
        console.error('[SubscriptionService] Backend sync failed:', validationError);
        // Continue anyway - RevenueCat has validated the purchase
        // The purchase is still valid, but backend sync failed
        // The app should refresh user profile after purchase to get updated status
      }

      // Update subscription cache after purchase so listeners receive the latest status
      await this.checkSubscriptionStatus();

      return transaction;
    } catch (error: any) {
      console.error('[SubscriptionService] Purchase error:', error);

      // Handle RevenueCat specific errors
      if (error.userCancelled !== undefined && error.userCancelled) {
        throw new SubscriptionError(
          'Purchase cancelled by user',
          'USER_CANCELLED'
        );
      }

      if (error.code) {
        throw new SubscriptionError(
          error.message || 'Purchase failed',
          error.code || 'PURCHASE_FAILED',
          error
        );
      }

      throw new SubscriptionError(
        error.message || 'Purchase failed',
        'PURCHASE_FAILED',
        error
      );
    }
  }

  /**
   * Restore previous purchases
   */
  async restorePurchases(): Promise<SubscriptionTransaction[]> {
    await this.initialize();

    if (!Capacitor.isNativePlatform() || !this.isInitialized) {
      throw new SubscriptionError(
        'Service not initialized or not on native platform',
        'NOT_INITIALIZED'
      );
    }

    try {
      const restoreResult = await Purchases.restorePurchases();
      const customerInfo = restoreResult.customerInfo;
      const transactions: SubscriptionTransaction[] = [];

      // Get active entitlements
      const activeEntitlements = Object.values(customerInfo.entitlements.active);

      for (const entitlement of activeEntitlements) {
        const productId = entitlement.productIdentifier;

        if (Object.values(CONFIG.PRODUCT_IDS).includes(productId)) {
          const transaction = await this.createTransactionFromCustomerInfo(
            customerInfo,
            productId,
            'restored'
          );
          transactions.push(transaction);
        }
      }

      // Sync with backend
      try {
        await this.syncWithBackend(customerInfo);
      } catch (error) {
        console.error('[SubscriptionService] Backend sync failed during restore:', error);
      }

      // Update subscription cache after restore
      await this.checkSubscriptionStatus();
      return transactions;
    } catch (error) {
      console.error('[SubscriptionService] Restore failed:', error);
      throw new SubscriptionError(
        'Failed to restore purchases',
        'RESTORE_FAILED',
        error
      );
    }
  }

  /**
   * Check current subscription status
   */
  async checkSubscriptionStatus(): Promise<UserSubscription | null> {
    await this.initialize();

    if (!Capacitor.isNativePlatform() || !this.isInitialized) {
      this.notifySubscriptionChange(null);
      return null;
    }

    try {
      const result = await Purchases.getCustomerInfo();
      const subscription = this.extractSubscriptionFromCustomerInfo(result.customerInfo);
      this.notifySubscriptionChange(subscription);
      return subscription;
    } catch (error) {
      console.error('[SubscriptionService] Failed to check subscription status:', error);
      this.notifySubscriptionChange(null);
      return null;
    }
  }

  /**
   * Refresh store data
   */
  async refreshStore(): Promise<void> {
    await this.initialize();

    if (!Capacitor.isNativePlatform() || !this.isInitialized) {
      return;
    }

    try {
      // Sync with RevenueCat to refresh customer info
      await Purchases.syncPurchases();
    } catch (error) {
      console.error('[SubscriptionService] Refresh failed:', error);
      throw new SubscriptionError(
        'Failed to refresh store',
        'REFRESH_FAILED',
        error
      );
    }
  }

  /**
   * Get subscription management URL
   */
  getSubscriptionManagementUrl(): string | null {
    const platform = Capacitor.getPlatform();

    if (platform === 'ios') {
      return 'https://apps.apple.com/account/subscriptions';
    } else if (platform === 'android') {
      return 'https://play.google.com/store/account/subscriptions';
    }

    return null;
  }

  /**
   * Open subscription management
   */
  async openSubscriptionManagement(): Promise<void> {
    const url = this.getSubscriptionManagementUrl();

    if (url) {
      window.open(url, '_blank');
    }
  }

  /**
   * Create transaction from RevenueCat CustomerInfo
   */
  private async createTransactionFromCustomerInfo(
    customerInfo: CustomerInfo,
    productId: string,
    state: 'purchased' | 'restored'
  ): Promise<SubscriptionTransaction> {
    // Find entitlement for this product
    let entitlement = customerInfo.entitlements.all[productId];

    // If not found, check active entitlements
    if (!entitlement) {
      const activeEntitlements = Object.values(customerInfo.entitlements.active);
      entitlement = activeEntitlements.find((e: any) => e.productIdentifier === productId) as any;
    }

    if (!entitlement) {
      // Fallback: create transaction with minimal info
      return {
        id: productId,
        userId: this.userId || '',
        productId,
        platform: Capacitor.getPlatform() as 'ios' | 'android',
        transactionId: productId,
        purchaseToken: undefined,
        originalTransactionId: undefined,
        state,
        purchaseDate: new Date(),
        expirationDate: undefined,
        receipt: undefined,
        validated: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }

    const latestTransaction = entitlement.latestPurchaseDate
      ? new Date(entitlement.latestPurchaseDate)
      : new Date();

    const expirationDate = entitlement.expirationDate
      ? new Date(entitlement.expirationDate)
      : undefined;

    // RevenueCat uses originalPurchaseDate or latestPurchaseDate for transaction IDs
    const transactionId = entitlement.originalPurchaseDate
      ? new Date(entitlement.originalPurchaseDate).getTime().toString()
      : productId;

    return {
      id: transactionId,
      userId: this.userId || '',
      productId,
      platform: Capacitor.getPlatform() as 'ios' | 'android',
      transactionId,
      purchaseToken: undefined, // RevenueCat handles this internally
      originalTransactionId: transactionId,
      state,
      purchaseDate: latestTransaction,
      expirationDate,
      receipt: undefined, // RevenueCat handles receipts internally
      validated: true, // RevenueCat validates automatically
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  /**
   * Create user subscription from RevenueCat CustomerInfo
   */
  private createUserSubscriptionFromCustomerInfo(
    customerInfo: CustomerInfo,
    productId: string
  ): UserSubscription {
    // Find entitlement for this product
    let entitlement = customerInfo.entitlements.all[productId];

    // If not found, check active entitlements
    if (!entitlement) {
      const activeEntitlements = Object.values(customerInfo.entitlements.active);
      entitlement = activeEntitlements.find((e: any) => e.productIdentifier === productId) as any;
    }

    if (!entitlement) {
      throw new Error(`No entitlement found for product ${productId}`);
    }

    const purchaseDate = entitlement.latestPurchaseDate
      ? new Date(entitlement.latestPurchaseDate)
      : new Date();

    const expirationDate = entitlement.expirationDate
      ? new Date(entitlement.expirationDate)
      : undefined;

    const isActive = entitlement.isActive || false;
    const status: SubscriptionStatus = isActive
      ? (expirationDate && expirationDate > new Date() ? 'active' : 'expired')
      : 'inactive';

    // RevenueCat uses originalPurchaseDate or latestPurchaseDate for transaction IDs
    const transactionId = entitlement.originalPurchaseDate
      ? new Date(entitlement.originalPurchaseDate).getTime().toString()
      : productId;

    return {
      id: transactionId,
      userId: this.userId || '',
      status,
      platform: Capacitor.getPlatform() as 'ios' | 'android',
      productId,
      currentTransactionId: transactionId,
      originalTransactionId: transactionId,
      purchaseDate,
      expirationDate,
      autoRenew: entitlement.willRenew || false,
      lastValidated: new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  private extractSubscriptionFromCustomerInfo(customerInfo: CustomerInfo): UserSubscription | null {
    const activeEntitlements = Object.values(customerInfo.entitlements.active);

    if (activeEntitlements.length === 0) {
      return null;
    }

    for (const entitlement of activeEntitlements as any[]) {
      const productId = entitlement.productIdentifier;
      if (Object.values(CONFIG.PRODUCT_IDS).includes(productId)) {
        return this.createUserSubscriptionFromCustomerInfo(customerInfo, productId);
      }
    }

    const firstEntitlement = activeEntitlements[0] as any;
    const productId = firstEntitlement.productIdentifier;
    return this.createUserSubscriptionFromCustomerInfo(customerInfo, productId);
  }

  /**
   * Sync customer info with backend (optional)
   * RevenueCat handles validation, but you may want to sync for your own records
   * This updates the backend with the purchase transaction and sets user profile subscription to 'active'
   */
  private async syncWithBackend(customerInfo: CustomerInfo): Promise<void> {
    if (!this.userId) {
      console.warn('[SubscriptionService] Cannot sync with backend: userId not set');
      return;
    }

    // Update user profile subscription status in Firestore via API
    // This ensures the backend usage-limits middleware recognizes the user as subscribed
    const subscription = this.extractSubscriptionFromCustomerInfo(customerInfo);
    if (subscription && subscription.status === 'active') {
      try {
        await apiClient.createUserProfile({
          subscription: 'active',
        } as any);
        console.log('[SubscriptionService] Backend subscription status synced successfully');
      } catch (error) {
        console.error('[SubscriptionService] Failed to sync subscription status with backend:', error);
        // Don't throw - the purchase is still valid via RevenueCat
      }
    }
  }

  /**
   * Log out from RevenueCat and reset subscription state.
   * Ensures previous user's subscription does not leak to a newly logged-in account.
   */
  async logOut(): Promise<void> {
    if (Capacitor.isNativePlatform() && this.isInitialized) {
      try {
        await Purchases.logOut();
        console.log('[SubscriptionService] Purchases logged out successfully');
      } catch (error) {
        console.warn('[SubscriptionService] Purchases.logOut failed:', error);
      }
    }
    this.userId = null;
    this.currentSubscription = null;
    this.notifySubscriptionChange(null);
  }
}

// Export singleton instance
export const subscriptionService = SubscriptionService.getInstance();

